import { useEffect, useState, useCallback } from "react";
import {
  ArrowLeft, Loader2, RefreshCw, Trophy, Clapperboard, Check, X, Minus,
  CheckCircle2, XCircle, Lightbulb, TrendingUp, BarChart3, ClipboardList, Handshake,
} from "lucide-react";
import { api } from "../lib/api";
import { TeamLogo } from "./TeamLogo";

const GRADE_COLOR = { A: "#10B981", B: "#34D399", C: "#F59E0B", D: "#F97316", F: "#EF4444" };
const CAT_ICON = { Market: TrendingUp, Stats: BarChart3, "Game Plan": ClipboardList };
const CAT_COLOR = { Market: "#A855F7", Stats: "#00E5FF", "Game Plan": "#F59E0B" };

const OutcomePill = ({ outcome }) => {
  const map = {
    hit: { c: "#10B981", Icon: Check, label: "Hit" },
    miss: { c: "#EF4444", Icon: X, label: "Miss" },
    push: { c: "#94A3B8", Icon: Minus, label: "Push" },
  };
  const { c, Icon, label } = map[outcome] || map.push;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold font-data"
      style={{ color: c, background: `${c}1a` }}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
};

const Scorecard = ({ label, correct, total }) => {
  const pct = total ? Math.round((correct / total) * 100) : 0;
  const color = pct >= 60 ? "#10B981" : pct >= 45 ? "#F59E0B" : "#EF4444";
  return (
    <div className="rounded-xl border border-[#233248] bg-[#0B0F17] p-3">
      <div className="text-[10px] font-data uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <b className="text-white text-xl tabular-nums font-data">{correct}/{total}</b>
        <span className="font-data text-sm font-bold" style={{ color }}>{pct}%</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-[#233248] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

const ConsensusParlayCard = ({ parlay }) => {
  if (!parlay || !parlay.legs || parlay.legs.length === 0) return null;
  const { legs, legs_total, legs_hit, cashed } = parlay;
  const allPush = legs_total === 0;
  const resultColor = cashed ? "#10B981" : allPush ? "#94A3B8" : "#EF4444";
  return (
    <div
      data-testid="consensus-parlay-result"
      className="rounded-2xl border p-4 mb-4"
      style={{ borderColor: `${resultColor}55`, background: `${resultColor}0d` }}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="flex items-center gap-1.5 font-heading font-bold text-white text-sm">
          <Handshake className="h-4 w-4 text-[#10B981]" /> Consensus Parlay · how it would have cashed
        </h3>
        <span
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 font-data text-xs font-bold"
          style={{ color: resultColor, background: `${resultColor}1a` }}
          data-testid="consensus-parlay-verdict"
        >
          {cashed ? <Trophy className="h-3.5 w-3.5" /> : allPush ? <Minus className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {allPush ? "All push" : cashed ? `CASHED ${legs_hit}/${legs_total}` : `${legs_hit}/${legs_total} legs hit`}
        </span>
      </div>
      <p className="text-[11px] text-slate-400 mb-3 font-data">
        The AI + Market agreement parlay we'd have built before this game ({legs.length} strongest legs), graded on the final box score.
      </p>
      <div className="space-y-1.5">
        {legs.map((l, i) => (
          <div
            key={i}
            data-testid={`consensus-parlay-leg-${i}`}
            className="flex items-center justify-between gap-2 rounded-lg border border-[#233248] bg-[#0B0F17] px-3 py-2"
          >
            <div className="min-w-0">
              <div className="text-white text-sm font-semibold truncate">
                {l.player_name}
                {l.strong && <span className="ml-1.5 text-[9px] font-data font-bold text-[#10B981] uppercase">strong</span>}
              </div>
              <div className="font-data text-[11px] text-slate-400">
                {l.side} {l.line} {l.metric} · mkt {l.market_pct}%
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-data text-xs text-slate-300">got <b className="text-white">{l.actual}</b></span>
              <OutcomePill outcome={l.outcome} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FilmRoom = ({ matchupId, eventId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback((refresh = false) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (eventId) params.set("event_id", eventId);
    if (refresh) params.set("refresh", "true");
    const qs = params.toString();
    api
      .get(`/results/${matchupId}/film${qs ? `?${qs}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not build the film room for this game."))
      .finally(() => setLoading(false));
  }, [matchupId, eventId]);

  useEffect(() => { load(); }, [load]);

  if (loading)
    return (
      <div className="rounded-2xl border border-[#233248] bg-[#141C2B] p-12 text-center">
        <Loader2 className="h-7 w-7 animate-spin text-[#00E5FF] mx-auto mb-3" />
        <h3 className="font-heading text-white font-bold">Studying the game tape…</h3>
        <p className="text-sm text-slate-400 mt-1">Grading every pick against the final box score and writing the breakdown.</p>
      </div>
    );

  if (error)
    return (
      <div>
        <button data-testid="film-back-button" onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to results
        </button>
        <div className="rounded-2xl border border-[#EF4444]/40 bg-[#EF4444]/5 p-8 text-center">
          <p className="text-slate-300">{error}</p>
        </div>
      </div>
    );

  if (!data) return null;

  const { home_team, away_team, final, winner, graded, analysis, consensus_parlay } = data;
  const a = analysis || {};
  const grade = (a.grade || "").toUpperCase();
  const patterns = a.patterns || [];

  return (
    <div data-testid="film-room">
      <button data-testid="film-back-button" onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to results
      </button>

      {/* Final score header */}
      <div className="rounded-2xl border border-[#233248] bg-[#141C2B] p-4 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="text-center">
              <TeamLogo sport={away_team.sport} abbr={away_team.abbr} color={away_team.color} size="xl" className="mx-auto" />
              <div className={`mt-1 font-data text-2xl font-bold tabular-nums ${winner === away_team.abbr ? "text-white" : "text-slate-500"}`}>
                {final.away_score}
              </div>
            </div>
            <span className="font-heading font-bold text-slate-600">@</span>
            <div className="text-center">
              <TeamLogo sport={home_team.sport} abbr={home_team.abbr} color={home_team.color} size="xl" className="mx-auto" />
              <div className={`mt-1 font-data text-2xl font-bold tabular-nums ${winner === home_team.abbr ? "text-white" : "text-slate-500"}`}>
                {final.home_score}
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B]/15 text-[#F59E0B] px-2.5 py-1 font-data text-xs font-bold">
              <Trophy className="h-3.5 w-3.5" /> {winner} won
            </span>
            <div className="font-data text-[11px] text-slate-500 mt-1">{final.short_detail}</div>
          </div>
        </div>
      </div>

      {/* AI headline + grade */}
      <div className="rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/10 to-[#141C2B] p-4 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="flex items-center gap-1.5 text-[10px] font-data font-bold uppercase tracking-wide text-[#00E5FF] mb-1">
              <Clapperboard className="h-3.5 w-3.5" /> Film Room · AI Review
            </span>
            <h2 className="font-heading text-lg font-extrabold text-white">{a.headline}</h2>
            <p className="text-sm text-slate-300 mt-1.5">{a.game_story}</p>
          </div>
          {grade && (
            <div className="shrink-0 text-center">
              <div
                className="h-14 w-14 rounded-xl grid place-items-center font-heading font-extrabold text-3xl"
                style={{ color: GRADE_COLOR[grade] || "#94A3B8", background: `${GRADE_COLOR[grade] || "#94A3B8"}1a`, border: `2px solid ${GRADE_COLOR[grade] || "#94A3B8"}` }}
              >
                {grade}
              </div>
              <div className="text-[9px] font-data uppercase tracking-wide text-slate-400 mt-1">AI grade</div>
            </div>
          )}
        </div>
      </div>

      {/* Scorecards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Scorecard label="AI / model picks" correct={final.model_correct} total={final.props_graded} />
        <Scorecard label="Market-implied side" correct={final.market_correct} total={final.props_graded} />
      </div>

      {/* Consensus parlay result */}
      <ConsensusParlayCard parlay={consensus_parlay} />

      {/* Right vs Missed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/5 p-4">
          <h3 className="flex items-center gap-1.5 font-heading font-bold text-[#10B981] text-sm mb-2">
            <CheckCircle2 className="h-4 w-4" /> What we got right
          </h3>
          <ul className="space-y-1.5">
            {(a.what_we_got_right || []).map((t, i) => (
              <li key={i} className="text-sm text-slate-300 flex gap-2"><Check className="h-4 w-4 text-[#10B981] shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-[#EF4444]/30 bg-[#EF4444]/5 p-4">
          <h3 className="flex items-center gap-1.5 font-heading font-bold text-[#EF4444] text-sm mb-2">
            <XCircle className="h-4 w-4" /> What we missed
          </h3>
          <ul className="space-y-1.5">
            {(a.what_we_missed || []).map((t, i) => (
              <li key={i} className="text-sm text-slate-300 flex gap-2"><X className="h-4 w-4 text-[#EF4444] shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Patterns to watch */}
      {patterns.length > 0 && (
        <div className="rounded-2xl border border-[#233248] bg-[#141C2B] p-4 mb-4">
          <h3 className="font-heading font-bold text-white text-sm mb-3">Patterns to watch next time</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {patterns.map((p, i) => {
              const Icon = CAT_ICON[p.category] || Lightbulb;
              const c = CAT_COLOR[p.category] || "#94A3B8";
              return (
                <div key={i} className="rounded-xl border border-[#233248] bg-[#0B0F17] p-3" style={{ boxShadow: `inset 3px 0 0 ${c}` }}>
                  <span className="flex items-center gap-1.5 text-[10px] font-data font-bold uppercase tracking-wide mb-1.5" style={{ color: c }}>
                    <Icon className="h-3.5 w-3.5" /> {p.category}
                  </span>
                  <p className="text-sm text-slate-300 leading-snug">{p.insight}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next time */}
      {(a.next_time || []).length > 0 && (
        <div className="rounded-2xl border border-[#A855F7]/30 bg-[#A855F7]/5 p-4 mb-4">
          <h3 className="flex items-center gap-1.5 font-heading font-bold text-[#C084FC] text-sm mb-2">
            <Lightbulb className="h-4 w-4" /> Adjustments for next time
          </h3>
          <ul className="space-y-1.5">
            {a.next_time.map((t, i) => (
              <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-[#C084FC] font-bold">{i + 1}.</span> {t}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Graded props table */}
      <div className="rounded-2xl border border-[#233248] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-[#0B0F17]">
          <h3 className="font-heading font-bold text-white text-sm">Graded player props</h3>
          <button
            data-testid="film-refresh-button"
            onClick={() => load(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#233248] text-slate-300 px-2.5 py-1 text-xs hover:border-[#00E5FF]/50"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Re-grade
          </button>
        </div>
        {graded.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8 px-4">
            No player props were loaded for this game, so there's nothing to grade — but the AI still broke down the result above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="graded-props-table">
              <thead>
                <tr className="bg-[#0B0F17] text-slate-400 text-left font-data text-[11px]">
                  <th className="px-3 py-2">Player</th>
                  <th className="px-3 py-2">Market</th>
                  <th className="px-3 py-2 text-right">Line</th>
                  <th className="px-3 py-2 text-right">Actual</th>
                  <th className="px-3 py-2 text-center">AI pick</th>
                  <th className="px-3 py-2 text-center">Market lean</th>
                </tr>
              </thead>
              <tbody>
                {graded.map((x, i) => (
                  <tr key={i} data-testid={`graded-row-${i}`} className="border-t border-[#233248]">
                    <td className="px-3 py-2">
                      <div className="text-white font-semibold">{x.player_name}</div>
                      <div className="font-data text-[10px] text-slate-500">{x.team_abbr} · {x.position}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-300">{x.metric}</td>
                    <td className="px-3 py-2 text-right font-data text-slate-300">{x.line}</td>
                    <td className="px-3 py-2 text-right font-data font-bold text-white">{x.actual}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="font-data text-[10px] text-slate-400 mb-0.5">{x.model_lean}</div>
                      <OutcomePill outcome={x.model_outcome} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="font-data text-[10px] text-slate-400 mb-0.5">{x.market_lean} {x.market_pct}%</div>
                      <OutcomePill outcome={x.market_outcome} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
